import React, {
  useState,
  createContext,
  useMemo,
  PropsWithChildren,
  useEffect,
  useContext,
} from "react";
import {
  CableSetup,
  Charger,
  CustomerDetails,
  QuoteDetailsResponse,
  StoreContext,
} from "_core/types";
import { CableLength, BaseColour } from "_core/types/charger";

const StoreContext = createContext<StoreContext>(
  undefined as any as StoreContext
);

const StoreProvider = ({ children }: PropsWithChildren) => {
  const [charger, setCharger] = useState<null | Charger>(null);
  const [utm_campaign, setCampaign] = useState<null | string>(null);
  const [utm_medium, setMedium] = useState<null | string>(null);
  const [utm_source, setSource] = useState<null | string>(null);

  const [postcode, setPostcode] = useState<null | string>(
    sessionStorage.getItem("postcode") || null
  );
  const [verificationCode, setVerificationCode] = useState<null | string>(
    sessionStorage.getItem("verificationCode") || null
  );
  const [confirm, setConfirm] = useState<null | string>(null);
  const [offStreetParking, setOffStreetParking] = useState<null | string>(null);
  const [cableSetup, setCableSetup] = useState<undefined | CableSetup>(
    undefined
  );
  const [customerDetails, setCustomerDetails] =
    useState<null | CustomerDetails>(null);
  const [externalId, setExternalId] = useState<null | string>(null);
  const [quoteDetails, setQuoteDetails] = useState<null | QuoteDetailsResponse>(
    null
  );
  const [cableLength, setCableLength] = useState<CableLength | undefined>(
    undefined
  );
  const [baseColour, setBaseColour] = useState<BaseColour | undefined>(
    undefined
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(
      window.location.search.split("?")[1]
    );

    const externalId = queryParams.get("externalId");
    if (externalId) setExternalId(externalId);

    const utm_campaign = queryParams.get("utm_campaign");
    if (utm_campaign) setCampaign(utm_campaign);

    const utm_medium = queryParams.get("utm_medium");
    if (utm_medium) setMedium(utm_medium);

    const utm_source = queryParams.get("utm_source");
    if (utm_source) setSource(utm_source);

    const selectedCharger = queryParams.get("charger")?.toLowerCase();

    if (selectedCharger === Charger.INDRA) {
      setCharger(Charger.INDRA);
    } else if (selectedCharger === Charger.HYPERVOLT) {
      setCharger(Charger.HYPERVOLT);
    } else if (selectedCharger === Charger.ANDERSENA2) {
      setCharger(Charger.ANDERSENA2);
    } else if (selectedCharger === Charger.ANDERSENA3) {
      setCharger(Charger.ANDERSENA3);
    } else if (selectedCharger === Charger.ANDERSENQUARTZ) {
      setCharger(Charger.ANDERSENQUARTZ);
    } else if (selectedCharger === Charger.ZAPPI21) {
      setCharger(Charger.ZAPPI21);
    } else if (selectedCharger === Charger.ZAPPIGLOW) {
      setCharger(Charger.ZAPPIGLOW);
    }

    const postcode = queryParams.get("postcode");
    if (postcode) setPostcode(postcode);

    const verificationCode = queryParams.get("confirm");

    if (verificationCode) setVerificationCode(verificationCode);
  }, [
    charger,
    postcode,
    utm_campaign,
    utm_medium,
    utm_source,
    verificationCode,
  ]);

  const contextValue = useMemo(() => {
    return {
      charger,
      postcode,
      verificationCode,
      confirm,
      offStreetParking,
      cableSetup,
      cableLength,
      baseColour,
      customerDetails,
      externalId,
      quoteDetails: quoteDetails,
      utm_campaign,
      utm_medium,
      utm_source,
      setCharger,
      setPostcode,
      setVerificationCode,
      setConfirm,
      setOffStreetParking,
      setCableSetup,
      setCableLength,
      setBaseColour,
      setCustomerDetails,
      setExternalId,
      setQuoteDetails: setQuoteDetails,
      setCampaign,
      setMedium,
      setSource,
    };
  }, [
    charger,
    postcode,
    verificationCode,
    confirm,
    offStreetParking,
    cableSetup,
    cableLength,
    baseColour,
    customerDetails,
    externalId,
    quoteDetails,
    utm_campaign,
    utm_medium,
    utm_source,
    setCharger,
    setPostcode,
    setVerificationCode,
    setConfirm,
    setOffStreetParking,
    setCableSetup,
    setCableLength,
    setBaseColour,
    setCustomerDetails,
    setExternalId,
    setQuoteDetails,
    setCampaign,
    setMedium,
    setSource,
  ]);

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};

const useStore = () => {
  const context = useContext(StoreContext);

  if (!context) throw new Error("useStore must be used within a StoreContext");

  return context;
};

export { useStore, StoreProvider };
